import { useEffect } from 'react'
import { initMetaPixel } from '../utils/metaPixel'

const INTERACTIONS = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const

/**
 * Margen tras el evento `load` antes de cargar el pixel por su cuenta.
 * Corto a propósito: con 6 s se perdían los PageView de quienes rebotaban
 * rápido sin tocar la página, y las métricas no coincidían con los clics del
 * anuncio.
 */
const FALLBACK_DELAY_MS = 1200

/**
 * Carga el Meta Pixel fuera de la ruta crítica.
 *
 * Los ≈200 KiB de scripts de Facebook bloqueaban el hilo principal durante la
 * carga (Total Blocking Time). Ahora se inicializa en la primera interacción
 * del visitante (toque, tecla o scroll) o, si nadie interactúa, unos segundos
 * después de que la página terminó de cargar. El PageView se sigue
 * registrando en toda visita real.
 */
export function useMetaPixel(): void {
  useEffect(() => {
    let done = false
    let timeout = 0

    const start = () => {
      if (done) return
      done = true
      cleanup()
      initMetaPixel()
    }

    const scheduleFallback = () => {
      timeout = window.setTimeout(() => {
        const idle = (
          window as typeof window & {
            requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void
          }
        ).requestIdleCallback
        if (idle) idle(start, { timeout: 1000 })
        else start()
      }, FALLBACK_DELAY_MS)
    }

    function cleanup() {
      INTERACTIONS.forEach((event) =>
        window.removeEventListener(event, start, { capture: true }),
      )
      window.removeEventListener('load', scheduleFallback)
      window.clearTimeout(timeout)
    }

    INTERACTIONS.forEach((event) =>
      window.addEventListener(event, start, {
        capture: true,
        once: true,
        passive: true,
      }),
    )

    if (document.readyState === 'complete') scheduleFallback()
    else window.addEventListener('load', scheduleFallback, { once: true })

    return cleanup
  }, [])
}
